// One event per HTTP call, from the two seams every HTTP call already passes.
//
// This package talks to exactly two backends, and each funnels every request
// through a single `sendRequest`: the vibe-server client's (autoAuctionClient.js)
// and the event runtime SDK's. Instrumenting those two functions is the whole
// API — request rate, latency and error rate PER ENDPOINT, without a call site
// anywhere having to remember to log.
//
// Per endpoint is the part that takes care. A path with an id in it
// (`/items/687f…`) is a unique string per vehicle, and logging it raw would
// produce one "endpoint" per asset — a facet with a million values, which is
// the same as no facet at all. `normalizeRequestPath` collapses the identifiers
// back out so `/items/:id` counts as one thing.
//
// The event runtime's SDK lives in a package this one does not own (see
// knowledge-base — the event runtime is consumed over HTTP, not edited), so it
// is instrumented from the outside by `instrumentApiSdk` rather than by an edit
// there. `configureBackends` is where that happens, because it is already the
// one place that touches that client. The vibe-server client is ours and calls
// `logRequestOutcome` directly, which is why the two report the same shape from
// different directions.
//
// Where the entries GO is the host's, through the logger port — see
// adapters/ports.js. Nothing here decides that; it decides what an HTTP call
// has to say for itself.

import { useLogger } from '../adapters/ports.js';

/** A path segment that identifies one record rather than naming an endpoint. */
const MONGO_OBJECT_ID = /^[0-9a-f]{24}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_DIGITS = /^\d+$/;
// Runtime badges and demo ids are neither — a long opaque token with digits in
// it is an identifier whatever its alphabet.
const OPAQUE_IDENTIFIER = /^(?=.*\d)[A-Za-z0-9_-]{12,}$/;

function isIdentifierSegment(segment) {
  return (
    MONGO_OBJECT_ID.test(segment)
    || UUID.test(segment)
    || ALL_DIGITS.test(segment)
    || OPAQUE_IDENTIFIER.test(segment)
  );
}

/**
 * The endpoint a URL names, with record identifiers replaced by `:id` and the
 * query string dropped. Accepts the string or `URL` both SDKs pass around, and
 * the relative `/api/...` the dev proxy produces.
 */
export function normalizeRequestPath(url) {
  let pathname;
  if (url && typeof url === 'object' && typeof url.pathname === 'string') {
    pathname = url.pathname;
  } else {
    const raw = String(url ?? '');
    try {
      // The base is only there to satisfy the parser for a relative path; an
      // absolute url ignores it.
      pathname = new URL(raw, 'http://request-logging.invalid').pathname;
    } catch {
      pathname = raw.split('?')[0];
    }
  }
  return pathname
    .split('/')
    .map((segment) => (isIdentifierSegment(segment) ? ':id' : segment))
    .join('/');
}

/** Monotonic where it exists, wall clock where it does not. */
export function requestTimestamp() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

// A failed request is not automatically an incident. A 404 for a vehicle that
// has gone, or a 401 on a stale token, is the app working — logging those at
// `error` trains everyone to ignore the level. A 5xx or a network throw is a
// different thing and keeps it.
function levelFor({ ok, status }) {
  if (ok) return 'info';
  if (typeof status === 'number' && status >= 400 && status < 500) return 'warn';
  return 'error';
}

/**
 * Emits the `http.request` event for one completed (or failed) call.
 *
 * `status` is omitted rather than guessed when the caller cannot know it — the
 * runtime SDK is wrapped from outside and only sees a status when a request
 * fails. `ok` is always known, and is the facet an error rate is built on.
 */
export function logRequestOutcome({ api, method, url, startedAt, status, ok, err }) {
  const durationMs = Math.round(requestTimestamp() - startedAt);
  const succeeded = ok ?? !err;
  const resolvedStatus = status ?? err?.statusCode;
  const fields = {
    event: 'http.request',
    api,
    method,
    path: normalizeRequestPath(url),
    ok: succeeded,
    durationMs
  };
  if (resolvedStatus != null) fields.status = resolvedStatus;
  if (err) fields.err = err;

  const level = levelFor({ ok: succeeded, status: resolvedStatus });
  useLogger()[level](fields, succeeded ? 'request completed' : 'request failed');
  return fields;
}

/**
 * Wraps a `sendRequest(url, method, body)` so every call through it reports.
 * Rethrows whatever it caught — instrumentation never changes an outcome.
 */
export function withRequestLogging(sendRequest, { api }) {
  return async function sendRequestWithLogging(url, method, body) {
    const startedAt = requestTimestamp();
    try {
      const result = await sendRequest(url, method, body);
      logRequestOutcome({ api, method, url, startedAt, ok: true });
      return result;
    } catch (err) {
      logRequestOutcome({ api, method, url, startedAt, ok: false, err });
      throw err;
    }
  };
}

// Instrumenting the same singleton twice would double every entry — a live
// hazard, because a hot reload re-runs the module that installs it.
const instrumentedSdks = new WeakSet();

/**
 * Instruments an API SDK singleton in place, from the outside. Every one of its
 * methods routes through `sendRequest`, so replacing that one property covers
 * the whole surface without touching the package it came from.
 */
export function instrumentApiSdk(sdk, { api }) {
  if (!sdk || typeof sdk.sendRequest !== 'function' || instrumentedSdks.has(sdk)) {
    return sdk;
  }
  // Bound, because the original reads private class fields off the instance and
  // is about to be called as a plain function.
  const original = sdk.sendRequest.bind(sdk);
  sdk.sendRequest = withRequestLogging(original, { api });
  instrumentedSdks.add(sdk);
  return sdk;
}

export const VIBE_SERVER_API = 'vibe-server';
export const EVENT_RUNTIME_API = 'event-runtime';
